/* Who is signed in, and what they may do.
 *
 * The session is an HttpOnly cookie the server sets on login; the page only
 * ever learns `{username, role}` from /auth/me. Three roles: a viewer
 * reads, an editor also hands in data files on the Data loads page (an
 * admin accepts them before they are loaded), an admin also loads files
 * straight from the pages, reviews the loads and manages users. The
 * context here is filled in by App.tsx once the session is known, and
 * `useCan` is the one question every page asks. */

import { createContext, useContext } from 'react'
import { post, request } from './api'

export type Role = 'viewer' | 'editor' | 'admin'
export interface User { username: string; role: Role }

export const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', editor: 'Editor', admin: 'Admin' }
export const ROLES: Role[] = ['viewer', 'editor', 'admin']

/** What a page may ask about the signed-in user. */
export type Ability = 'load' | 'upload' | 'review' | 'manage'
const ALLOWED: Record<Ability, Role[]> = {
  /** Hand in a data file on the Data loads page. */
  load: ['editor', 'admin'],
  /** Load a file from the page it belongs to, straight into the database. */
  upload: ['admin'],
  /** Accept, approve and delete loads. */
  review: ['admin'],
  /** Manage users. */
  manage: ['admin'],
}
export const can = (user: User | null, what: Ability) =>
  user !== null && ALLOWED[what].includes(user.role)

// ---------------------------------------------------------------- context
export interface AuthState {
  user: User
  signOut: () => void
  /** Loads awaiting an admin's look, for the rail badge; 0 for a viewer. */
  pending: number
  setPending: (n: number) => void
}

export const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside the signed-in portal')
  return ctx
}

export const useCan = (what: Ability) => can(useAuth().user, what)

// -------------------------------------------------------------------- api
export const auth = {
  me: () => request<User>('/auth/me'),
  login: (username: string, password: string) => post<User>('/auth/login', { username, password }),
  logout: () => post<unknown>('/auth/logout'),
}

export interface Load {
  id: number
  filename: string
  kind: string
  uploaded_at: string
  uploaded_by: string | null
  days: number | null
  first_date: string | null
  last_date: string | null
  rows: number | null
  rows_replaced: number | null
  warnings: string[]
  status: 'pending' | 'approved'
  reviewed_by: string | null
  reviewed_at: string | null
  /** On disk, waiting for an admin to accept it; nothing loaded yet. */
  staged: boolean
}

/** One kind of file the Data loads page takes. */
export interface LoadKind {
  id: string
  label: string
  blurb: string
  /** Extensions, like '.xlsx'. */
  accept: string[]
  multiple: boolean
}

export const loads = {
  list: () => request<{ loads: Load[]; pending: number }>('/loads'),
  kinds: () => request<{ kinds: LoadKind[] }>('/loads/kinds').then((r) => r.kinds),
  /** Hands the files in. An admin's are loaded at once; an editor's are
   *  staged until an admin accepts them. `month` is for a workers'
   *  register whose file name does not carry its month. */
  upload: (kind: string, files: File[], month?: string) => {
    const fd = new FormData()
    fd.append('kind', kind)
    if (month) fd.append('month', month)
    files.forEach((f) => fd.append('files', f))
    return request<{
      ok: boolean; loaded: Load[]; failed: { filename: string; error: string }[]; pending: number
    }>('/loads/upload', { method: 'POST', body: fd })
  },
  /** Accepts a staged file (loads it) or approves a loaded one. */
  approve: (id: number) => post<Load>(`/loads/${id}/approve`),
  /** Rejects a staged file, or rolls a load's rows back; `removed` is rows by table. */
  remove: (id: number) =>
    request<{ removed: Record<string, number>; note?: string | null }>(`/loads/${id}`, { method: 'DELETE' }),
}

export interface Account {
  id: number
  username: string
  role: Role
  active: boolean
  created_at: string
}

export const users = {
  list: () => request<{ users: Account[] }>('/users').then((r) => r.users),
  create: (body: { username: string; password: string; role: Role }) => post<Account>('/users', body),
  update: (id: number, patch: { role?: Role; active?: boolean; password?: string }) =>
    request<Account>(`/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
}
